<template>
  <div :class="`input-wrapper ${id} ${hasErrors ? 'has-errors' : ''} ${isDisabled ? 'disabled' : ''}`">
    <!-- input label -->
    <label v-if="label" :class="['input-label', { 'visually-hidden': hideLabel }]" :for="id">
      <span>{{ label }}</span>
    </label>

    <!-- input element -->
    <div class="input-el-wrapper">
      <input
        :id="id"
        ref="inputElRef"
        class="input"
        :type="type"
        v-model="valueRef"
        :data-value="valueRef"
        :placeholder="placeholder"
        :min="type === 'date' ? minDate : undefined"
        :max="type === 'date' ? maxDate : undefined"
        :aria-label="controlAriaLabel"
        @input="onChange"
        @click="onClick"
      />

      <!-- input aside elements -->
      <div v-if="type === 'tel' && countryCode" class="input-flag-wrapper">
        <CountryFlag :country="countryCode" />
      </div>

      <div v-if="type === 'password'" class="input-password-wrapper" @click="onClickShowPassword">
        <ShowIcon v-if="isPasswordVisible" color="danger" filled />
        <HideIcon v-else color="disabled" filled />
      </div>
    </div>

    <div v-if="errors" class="input-errors-wrapper" data-test-id="input-errors">{{ errors }}</div>
  </div>
</template>

<script setup lang="ts">
import CountryFlag from 'vue-country-flag-next'
import { computed, ref, watch } from 'vue'

import HideIcon from '@/features/ui/components/icons/hide-icon.vue'
import ShowIcon from '@/features/ui/components/icons/show-icon.vue'
import debounce from '@/utils/misc/debounce'

interface InputPropsType {
  id: string
  errors?: string
  label?: string
  /** Masque le label visuellement tout en le laissant associé (for/id). */
  hideLabel?: boolean
  /** Nom accessible si le champ n'a pas de label visible (dernier recours). */
  ariaLabel?: string
  countryCode?: string | null
  type?: string
  placeholder: string
  value?: string | null
  defaultOnChangeDelay?: number
  minDate?: string
  maxDate?: string
  isDisabled?: boolean
}

const props = defineProps<InputPropsType>()

// set default value v-model
const valueRef = ref(props.value || '')

watch(
  () => props.value,
  (newVal) => {
    valueRef.value = newVal ?? ''
  },
)

const isPasswordVisible = ref(false)
const inputElRef = ref<HTMLInputElement | null>(null)

defineExpose({
  clear() {
    valueRef.value = ''
  },
  set(message: string) {
    valueRef.value = message
  },
})

const emit = defineEmits(['emit:change'])

const onChange = debounce(
  (event: Event) => {
    const value = (event.target as HTMLInputElement).value
    // Emit the new value to the parent with the event name 'update:modelValue'
    emit('emit:change', value)
  },
  props.defaultOnChangeDelay || 150,
  false,
)

const onClick = (event: Event) => {
  if (props.type === 'date' || props.type === 'time') {
    const input = event.target as HTMLInputElement
    try {
      input.showPicker()
    } catch (error) {
      // Silently fail if showPicker() is not allowed (e.g., in tests or without user gesture)
      console.debug('showPicker() failed:', error)
    }
  }
}

const onClickShowPassword = () => {
  const input = inputElRef.value
  if (input) {
    input.type = input.type === 'password' ? 'text' : 'password'
  }
  isPasswordVisible.value = !isPasswordVisible.value
}

const hasErrors = computed(() => !!props.errors)
const controlAriaLabel = computed(() => (props.label ? undefined : props.ariaLabel || props.placeholder))
</script>

<style scoped lang="scss">
@import './custom-input.scss';
</style>
